import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { surveysAdmin } from "../../lib/adminQueues";
import { SURVEY_AUDIENCES, SURVEY_SHORT } from "../../lib/surveys";
import { FeedbackSummary } from "./feedback/FeedbackSummary";
import { SurveyEditor } from "./feedback/SurveyEditor";
import { DigestSettings } from "./feedback/DigestSettings";
import { BetaStudentsTable } from "./feedback/BetaStudentsTable";

const WINDOWS = [7, 30, 90, 365];

const Section = ({ title, blurb, children, testid }) => (
  <section data-testid={testid}>
    <h3 className="font-serif text-2xl leading-tight text-ink">{title}</h3>
    {blurb && <p className="mt-1 text-sm text-olive">{blurb}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

/** Admin → Feedback: numbers, comments, beta-student gate, question editors, summary settings. */
export const FeedbackTab = () => {
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState(30);
  const [editing, setEditing] = useState("donor");
  const highlight = useMemo(() => new URLSearchParams(window.location.search).get("response"), []);

  const loadConfig = useCallback(() => surveysAdmin.config().then(setConfig).catch(() => {}), []);
  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { surveysAdmin.summary(days).then(setSummary).catch(() => {}); }, [days]);
  useEffect(() => {
    if (!highlight || !summary) return;
    document.getElementById(`response-${highlight}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlight, summary]);

  const save = async (patch) => setConfig(await surveysAdmin.saveConfig(patch));

  if (!config) return <div className="flex items-center gap-2 text-olive"><Loader2 className="h-5 w-5 animate-spin" /> Loading feedback…</div>;

  return (
    <div data-testid="tab-feedback" className="space-y-12">
      <Section
        title="What donors and students are telling us"
        blurb="Anonymous unless the person chose to leave an email. Scores of 1–2 and comments with problem words email you immediately; everything rolls into the weekly summary."
        testid="feedback-section-summary"
      >
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {WINDOWS.map((w) => (
            <button key={w} type="button" onClick={() => setDays(w)} data-testid={`feedback-window-${w}`} className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] ${days === w ? "bg-ink text-sand" : "bg-ink/5 text-olive hover:bg-ink/10"}`}>
              {w === 365 ? "Year" : `${w} days`}
            </button>
          ))}
        </div>
        {summary ? <FeedbackSummary summary={summary} highlight={highlight} /> : <p className="text-sm text-olive">Loading…</p>}
      </Section>

      <Section title="Beta students and the final installment" blurb="Students see two pop-ups: a short one right after they submit (optional) and the beta survey once their first grant lands. The post-grant survey must be in before the final installment is released. Waive only when you have what you need another way." testid="feedback-section-students">
        <BetaStudentsTable />
      </Section>

      <Section title="Survey questions" blurb="Edit the wording, add or remove questions, reorder. Changes apply to the next person who sees the survey." testid="feedback-section-editor">
        <div className="mb-4 flex flex-wrap gap-2">
          {SURVEY_AUDIENCES.map((a) => (
            <button key={a} type="button" onClick={() => setEditing(a)} data-testid={`feedback-edit-${a}`} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${editing === a ? "bg-ink text-sand" : "bg-ink/5 text-ink hover:bg-ink/10"}`}>
              {SURVEY_SHORT[a]}
            </button>
          ))}
        </div>
        <SurveyEditor
          key={`${editing}-${config.updated_at || ""}`}
          audience={editing}
          config={config[editing]}
          defaults={config.defaults[editing]}
          onSave={save}
        />
      </Section>

      <Section title="Weekly summary and alerts" blurb="Who gets the summary, when it goes out, and what triggers an immediate alert." testid="feedback-section-digest">
        <DigestSettings config={config} onSave={save} />
      </Section>
    </div>
  );
};
